import React, { useState } from "react";
import { Lock } from "lucide-react";

/**
 * ComingSoonWrapper - A wrapper component for features that are under development
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The menu item or component to wrap
 * @param {boolean} props.isComingSoon - Whether to show "coming soon" state
 * @param {string} props.featureName - Name of the feature for the tooltip/modal
 * @param {string} props.estimatedRelease - Optional estimated release date
 * @param {function} props.onClick - Original onClick handler (will be overridden if isComingSoon is true)
 * @param {string} props.className - Additional CSS classes to apply
 */
const ComingSoonWrapper = ({
  children,
  isComingSoon = false,
  featureName = "This feature",
  estimatedRelease = "",
  onClick,
  className = "",
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // If not coming soon, render children as-is
  if (!isComingSoon) {
    return <div onClick={onClick} className={className}>{children}</div>;
  }

  // Handle click for coming soon features
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  return (
    <>
      {/* Wrapper with disabled state */}
      <div
        className={`relative ${className}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={handleClick}
      >
        {/* Semi-transparent overlay to show disabled state */}
        <div className="opacity-50 cursor-not-allowed pointer-events-none">
          {children}
        </div>

        {/* Lock icon overlay */}
        <div className="absolute top-1/2 right-2 transform -translate-y-1/2 pointer-events-none">
          <Lock size={14} className="text-gray-400 dark:text-gray-500" />
        </div>

        {/* Tooltip on hover */}
        {showTooltip && (
          <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 z-50 pointer-events-none">
            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
              Coming Soon
              <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
            </div>
          </div>
        )}
      </div>

      {/* Modal for more details */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="bg-accent1/10 dark:bg-accent1/20 p-4 rounded-full">
                <Lock size={32} className="text-accent1" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-2">
              Coming Soon!
            </h3>

            {/* Description */}
            <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
              {featureName} is currently under development and will be available
              soon.
            </p>

            {/* Estimated Release */}
            {estimatedRelease && (
              <div className="bg-accent1/5 dark:bg-accent1/10 rounded-lg p-3 mb-4">
                <p className="text-sm text-center text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Estimated Release:</span>{" "}
                  {estimatedRelease}
                </p>
              </div>
            )}

            {/* Features list (optional customization) */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                We're working hard to bring you this feature. Stay tuned for
                updates!
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              className="w-full bg-accent1 hover:bg-accent1/90 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ComingSoonWrapper;
